import React, { useEffect, useState } from "react"
import { useTranslation } from 'react-i18next'
import { Modal } from 'react-bootstrap'
import ReactApexChart from 'react-apexcharts'

import { httpOk, GET } from 'helpers/api'
import { returnSign, formatDate } from 'helpers/helpers'

/**
 * Сводка по посещаемости за выбранный период: часы по должностям, кто приходит в своё
 * обычное время, сколько часов недостоверны.
 *
 * Про «вовремя»: план-график (work_schedule_shifts) заведения не заполняют, сравнивать
 * с планом нечего. Поэтому норма — обычное время самого сотрудника (медиана прихода за
 * период), и подписано это именно так: цифра показывает разброс, а не опоздание против
 * плана. Заполнят график — бэк отдаст другой baseline.
 *
 * Смены длиннее порога в часы не идут вовсе: у них не зарегистрирован выход, и в их
 * длительности лежит пауза между приходами. Показываем их отдельной цифрой.
 */

function secondsToHm(seconds) {
	const total = Number(seconds) || 0
	const hours = Math.floor(total / 3600)
	const minutes = Math.floor((total % 3600) / 60)
	return `${hours}:${String(minutes).padStart(2, '0')}`
}

function secondsToClock(seconds) {
	if (seconds === null || seconds === undefined) return '—'
	const total = Number(seconds) || 0
	const hours = Math.floor(total / 3600) % 24
	const minutes = Math.floor((total % 3600) / 60)
	return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`
}

function percent(part, total) {
	if (!total) return 0
	return Math.round((Number(part) || 0) * 100 / Number(total))
}

function Analytics({ show, onHide, filterData }) {
	const { t } = useTranslation()

	const [data, setData] = useState(null)

	async function getAnalytics() {
		var urlParams = ""
		if (filterData.pos_id)
			urlParams += returnSign(urlParams) + 'pos_id=' + filterData.pos_id
		if (filterData.start_date)
			urlParams += returnSign(urlParams) + 'start_date=' + filterData.start_date
		if (filterData.end_date)
			urlParams += returnSign(urlParams) + 'end_date=' + filterData.end_date

		const response = await GET('/users-attendances-analytics' + urlParams, {}, { loader: true })
		if (httpOk(response)) setData(response.data)
	}

	useEffect(() => {
		if (show) getAnalytics()
		if (!show) setData(null)
	}, [show]) // eslint-disable-line react-hooks/exhaustive-deps

	const summary = data?.summary
	const byEmployee = data?.by_employee ?? []

	// «Вовремя» по заведению — доля от смен, по которым норму вообще можно посчитать
	const punctuality = byEmployee.reduce((acc, item) => {
		acc.on_time += Number(item.on_time) || 0
		acc.late += Number(item.late) || 0
		acc.short_shifts += Number(item.short_shifts) || 0
		return acc
	}, { on_time: 0, late: 0, short_shifts: 0 })
	const punctualityBase = punctuality.on_time + punctuality.late

	const chartSeries = [
		{
			name: t('hours'),
			data: (data?.by_date ?? []).map(item => Number((Number(item.seconds_total) / 3600).toFixed(1))),
		},
	]
	const chartOptions = {
		chart: { type: 'bar', toolbar: { show: false } },
		dataLabels: { enabled: false },
		xaxis: {
			categories: (data?.by_date ?? []).map(item => formatDate(item.work_date, 'dd.MM')),
		},
		colors: ['#5b73e8'],
		grid: { borderColor: '#f1f1f1' },
	}

	return (
		<Modal show={show} animation={false} centered dialogClassName="modal-xl" onHide={onHide}>
			<Modal.Header closeButton>
				<Modal.Title>
					{t('attendance_analytics')}
					{filterData?.start_date &&
						<span className="text-muted fs-6 ms-2">
							{formatDate(filterData.start_date, 'dd.MM.yyyy')} — {formatDate(filterData.end_date, 'dd.MM.yyyy')}
						</span>
					}
				</Modal.Title>
			</Modal.Header>
			<Modal.Body>
				{!data &&
					<div className="text-center text-muted py-4">{t('loading')}</div>
				}

				{data &&
					<>
						<div className="row g-2 mb-3">
							<div className="col-6 col-md-3">
								<div className="card mb-0 h-100">
									<div className="card-body py-2">
										<div className="text-muted">{t('employees')}</div>
										<div className="fw-bold fs-4">{summary.employees}</div>
									</div>
								</div>
							</div>
							<div className="col-6 col-md-3">
								<div className="card mb-0 h-100">
									<div className="card-body py-2">
										<div className="text-muted">{t('total')}, {t('hours')}</div>
										<div className="fw-bold fs-4">{secondsToHm(summary.seconds_total - summary.seconds_suspicious)}</div>
									</div>
								</div>
							</div>
							<div className="col-6 col-md-3">
								<div className="card mb-0 h-100">
									<div className="card-body py-2">
										<div className="text-muted">{t('attendance_avg_shift')}</div>
										<div className="fw-bold fs-4">{secondsToHm(summary.seconds_avg_shift)}</div>
									</div>
								</div>
							</div>
							<div className="col-6 col-md-3">
								<div className="card mb-0 h-100">
									<div className="card-body py-2">
										<div className="text-muted">{t('attendance_on_time_share')}</div>
										<div className="fw-bold fs-4">{percent(punctuality.on_time, punctualityBase)}%</div>
									</div>
								</div>
							</div>
						</div>

						{/* Недостоверные часы — первым делом: пока их не разобрали, остальные
						    цифры владельцу верить нельзя */}
						{Number(summary.shifts_suspicious) > 0 &&
							<div className="alert alert-warning mb-3">
								<div className="fw-bold">
									<i className="uil-exclamation-triangle me-1"></i>
									{t('attendance_suspicious_summary', {
										shifts: summary.shifts_suspicious,
										hours: secondsToHm(summary.seconds_suspicious),
										limit: data.suspicious_shift_hours,
									})}
								</div>
								<div className="mt-1">{t('attendance_suspicious_excluded')}</div>
							</div>
						}

						{Number(summary.shifts_open) > 0 &&
							<div className="alert alert-secondary mb-3">
								{t('attendance_open_shifts', { count: summary.shifts_open })}
							</div>
						}

						<div className="alert alert-light border mb-3">
							<i className="uil-info-circle me-1"></i>
							{t('attendance_baseline_hint', { minutes: data.punctuality_tolerance_minutes })}
						</div>

						{(data.by_date ?? []).length > 0 &&
							<div className="card mb-3">
								<div className="card-body">
									<h6 className="mb-2">{t('attendance_hours_by_date')}</h6>
									<ReactApexChart options={chartOptions} series={chartSeries} type="bar" height={200} />
								</div>
							</div>
						}

						<div className="card mb-3">
							<div className="card-body">
								<h6 className="mb-2">{t('attendance_hours_by_position')}</h6>
								<div className="table-responsive">
									<table className="table table-bordered mb-0">
										<thead>
											<tr>
												<th>{t('position')}</th>
												<th className="text-center">{t('employees')}</th>
												<th className="text-center">{t('shifts')}</th>
												<th className="text-end">{t('hours')}</th>
												<th className="text-end">{t('attendance_avg_shift')}</th>
												<th className="text-end">{t('attendance_suspicious_total')}</th>
											</tr>
										</thead>
										<tbody>
											{(data.by_position ?? []).map((item, index) => (
												<tr key={index}>
													<td>{item.group_name}</td>
													<td className="text-center">{item.employees}</td>
													<td className="text-center">{item.shifts_total}</td>
													<td className="text-end"><b>{secondsToHm(item.seconds_total)}</b></td>
													<td className="text-end">{secondsToHm(item.seconds_avg_shift)}</td>
													<td className="text-end">
														{Number(item.shifts_suspicious) > 0 &&
															<span className="text-warning">{item.shifts_suspicious}</span>
														}
													</td>
												</tr>
											))}
										</tbody>
									</table>
								</div>
							</div>
						</div>

						<div className="card mb-0">
							<div className="card-body">
								<h6 className="mb-2">{t('attendance_by_employee')}</h6>
								<div className="table-responsive">
									<table className="table table-bordered mb-0">
										<thead>
											<tr>
												<th>{t('FIO')}</th>
												<th>{t('position')}</th>
												<th className="text-center">{t('shifts')}</th>
												<th className="text-end">{t('hours')}</th>
												<th className="text-center">{t('attendance_usual_check_in')}</th>
												<th className="text-center">{t('attendance_usual_shift')}</th>
												<th className="text-center">{t('attendance_on_time')}</th>
												<th className="text-center">{t('attendance_later_than_usual')}</th>
												<th className="text-center">{t('attendance_shorter_than_usual')}</th>
												<th className="text-center">{t('attendance_suspicious_total')}</th>
											</tr>
										</thead>
										<tbody>
											{byEmployee.map((item, index) => (
												<tr key={index}>
													<td className="td-ellipsis" title={item.name}>{item.name}</td>
													<td className="td-ellipsis" title={item.group_name}>{item.group_name}</td>
													<td className="text-center">{item.shifts_total}</td>
													<td className="text-end"><b>{secondsToHm(item.seconds_total)}</b></td>
													<td className="text-center">{secondsToClock(item.usual_check_in_seconds)}</td>
													<td className="text-center">{secondsToHm(item.usual_shift_seconds)}</td>
													<td className="text-center text-success">{item.on_time}</td>
													<td className="text-center">
														{Number(item.late) > 0
															? <span className="text-danger">{item.late}</span>
															: 0}
													</td>
													<td className="text-center">{item.short_shifts}</td>
													<td className="text-center">
														{Number(item.shifts_suspicious) > 0 &&
															<span className="text-warning">{item.shifts_suspicious}</span>
														}
													</td>
												</tr>
											))}
										</tbody>
									</table>
								</div>
							</div>
						</div>
					</>
				}
			</Modal.Body>
		</Modal>
	)
}

export default Analytics
